import { useConsole, type LogEntry } from '../state/console.js';
import { usePlay } from '../state/play.js';

/**
 * The Console's automatic actions (Unity's Error Pause, Clear on Play and
 * Clear on Build), driven by the per-user console preferences:
 *
 * - Error Pause: an error logged while Play runs pauses it;
 * - Clear on Play: the log is cleared when Play starts (`starting`);
 * - Clear on Build: the log is cleared on the `prism:build-start` DOM event
 *   (dispatched by the build window when a build begins).
 *
 * Installed once by the Workspace; returns the uninstaller.
 */

/** Errors logged (repeats included). */
export function errorCount(entries: readonly LogEntry[]): number {
  let n = 0;
  for (const e of entries) if (e.level === 'error') n += e.count;
  return n;
}

/** Whether an update of the log added an error (new line or a repeat of one). */
export function addedError(before: readonly LogEntry[], after: readonly LogEntry[]): boolean {
  if (after === before) return false;
  const counts = new Map<number, number>();
  for (const e of before) if (e.level === 'error') counts.set(e.id, e.count);
  return after.some((e) => e.level === 'error' && e.count > (counts.get(e.id) ?? 0));
}

export const BUILD_START_EVENT = 'prism:build-start';

export function startConsoleBehaviours(target: Pick<Window, 'addEventListener' | 'removeEventListener'> | null = typeof window === 'undefined' ? null : window): () => void {
  const offConsole = useConsole.subscribe((state, prev) => {
    if (!state.prefs.errorPause || !addedError(prev.entries, state.entries)) return;
    const p = usePlay.getState();
    if (p.state === 'playing') p.togglePause();
  });
  const offPlay = usePlay.subscribe((state, prev) => {
    if (state.state === 'starting' && prev.state !== 'starting' && useConsole.getState().prefs.clearOnPlay) useConsole.getState().clear();
  });
  const onBuild = () => {
    if (useConsole.getState().prefs.clearOnBuild) useConsole.getState().clear();
  };
  target?.addEventListener(BUILD_START_EVENT, onBuild);
  return () => {
    offConsole();
    offPlay();
    target?.removeEventListener(BUILD_START_EVENT, onBuild);
  };
}
